/**
 * NotificationItem Component - AccountsGenie
 * Individual notification item with actions and styling
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  CreditCard,
  FileText,
  Settings,
  Eye,
  EyeOff,
  Trash2,
  ExternalLink
} from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';
import { cn } from '../../lib/utils';
import { Notification, NotificationType, NotificationPriority } from '../../types/notifications';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onMarkAsUnread?: (id: string) => void;
  onDelete?: (id: string) => void;
  onAction?: (notification: Notification) => void;
  compact?: boolean;
  showActions?: boolean;
  className?: string;
}

const getNotificationIcon = (type: NotificationType): React.ReactNode => {
  const iconClass = "h-4 w-4 flex-shrink-0";
  
  switch (type) {
    case 'success':
      return <CheckCircle className={cn(iconClass, "text-green-600")} />;
    case 'warning':
      return <AlertTriangle className={cn(iconClass, "text-yellow-600")} />;
    case 'error':
      return <XCircle className={cn(iconClass, "text-red-600")} />;
    case 'payment':
      return <CreditCard className={cn(iconClass, "text-blue-600")} />;
    case 'report':
      return <FileText className={cn(iconClass, "text-purple-600")} />;
    case 'system':
      return <Settings className={cn(iconClass, "text-gray-600")} />;
    case 'info':
    default:
      return <Info className={cn(iconClass, "text-blue-600")} />;
  }
};

const getPriorityColor = (priority: NotificationPriority): string => {
  switch (priority) {
    case 'critical':
      return 'border-l-red-500 bg-red-50 dark:bg-red-950/20';
    case 'high':
      return 'border-l-orange-500 bg-orange-50 dark:bg-orange-950/20';
    case 'medium':
      return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-950/20';
    case 'low':
    default:
      return 'border-l-blue-500 bg-blue-50 dark:bg-blue-950/20';
  }
};

const getPriorityBadgeVariant = (priority: NotificationPriority) => {
  switch (priority) {
    case 'critical':
      return 'destructive';
    case 'high':
      return 'default';
    case 'medium':
      return 'secondary';
    case 'low':
    default:
      return 'outline';
  }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onMarkAsUnread,
  onDelete,
  onAction,
  compact = false,
  showActions = true,
  className
}) => {
  const navigate = useNavigate();

  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    if (onAction) {
      onAction(notification);
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  const handleMarkAsUnread = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkAsUnread) {
      onMarkAsUnread(notification.id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(notification.id);
    }
  };

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });

  return (
    <div
      className={cn(
        "relative p-3 border-l-4 transition-all duration-200 hover:shadow-sm",
        getPriorityColor(notification.priority),
        !notification.isRead && "border-l-4",
        notification.isRead && "opacity-75",
        compact ? "p-2" : "p-3",
        className
      )}
      role="article"
      aria-label={`Notification: ${notification.title}`}
    >
      <div className="flex items-start space-x-3">
        {/* Notification Icon */}
        <div className="mt-0.5">
          {getNotificationIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {/* Title and Priority */}
              <div className="flex items-center space-x-2 mb-1">
                <h4 className={cn(
                  "font-medium text-sm truncate",
                  !notification.isRead ? "text-foreground" : "text-muted-foreground"
                )}>
                  {notification.title}
                </h4>
                {!compact && notification.priority !== 'low' && (
                  <Badge
                    variant={getPriorityBadgeVariant(notification.priority)}
                    className="text-xs px-1.5 py-0.5"
                  >
                    {notification.priority.toUpperCase()}
                  </Badge>
                )}
                {!notification.isRead && (
                  <div 
                    className="h-2 w-2 bg-blue-600 rounded-full flex-shrink-0"
                    aria-label="Unread notification"
                  />
                )}
              </div>

              {/* Message */}
              <p className={cn(
                "text-sm leading-relaxed",
                compact ? "line-clamp-1" : "line-clamp-2",
                !notification.isRead ? "text-foreground" : "text-muted-foreground"
              )}>
                {notification.message}
              </p>

              {/* Timestamp and Action */}
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-muted-foreground">
                  {timeAgo}
                </span>
                
                {notification.actionUrl && notification.actionText && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAction}
                    className="h-auto p-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/20"
                  >
                    {notification.actionText}
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
            </div>

            {/* Actions */}
            {showActions && !compact && (
              <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                {notification.isRead ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMarkAsUnread}
                    className="h-8 w-8 p-0 opacity-60 hover:opacity-100"
                    title="Mark as unread"
                    aria-label="Mark as unread"
                  >
                    <EyeOff className="h-3 w-3" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMarkAsRead}
                    className="h-8 w-8 p-0 opacity-60 hover:opacity-100"
                    title="Mark as read"
                    aria-label="Mark as read"
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDelete}
                  className="h-8 w-8 p-0 opacity-60 hover:opacity-100 hover:text-red-600"
                  title="Delete notification"
                  aria-label="Delete notification"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;